import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import * as echarts from 'echarts';
import { useEEGSQIData, useConnectionState } from '../../../stores/processedDataStore';
import { useTranslation } from "../../../locales";
import { useLanguageStore } from "../../../stores/languageStore";

interface SignalQualityChartProps {
  channel?: 'ch1' | 'ch2' | 'both';
  title?: string;
}

const SignalQualityChart: React.FC<SignalQualityChartProps> = ({ 
  channel = 'both',
  title 
}) => {
  const { currentLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const eegSQIData = useEEGSQIData();
  const isConnected = useConnectionState();
  const [isInitialized, setIsInitialized] = useState(false);

  // EEG 분석 결과 기반 SQI 데이터가 있는지 확인 (조건 완화)
  const hasRealData = eegSQIData?.ch1SQI?.length > 0 || eegSQIData?.ch2SQI?.length > 0;




  // 차트 옵션을 useMemo로 최적화
  const chartOption = useMemo(() => {
    // 채널별 시리즈 설정
    const series: echarts.LineSeriesOption[] = [];
    const legend: string[] = [];
    
    if (channel === 'both' || channel === 'ch1') {
      series.push({
        name: t.visualizer.ch1SeriesFP1 + ' SQI',
        type: 'line',
        data: [],
        lineStyle: {
          color: '#10b981',
          width: 2
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(16, 185, 129, 0.3)' },
              { offset: 1, color: 'rgba(16, 185, 129, 0.1)' }
            ]
          }
        },
        symbol: 'none',
        animation: false
      });
      legend.push(t.visualizer.ch1SeriesFP1 + ' SQI');
    }
    
    if (channel === 'both' || channel === 'ch2') {
      series.push({
        name: t.visualizer.ch2SeriesFP2 + ' SQI',
        type: 'line',
        data: [],
        lineStyle: {
          color: '#f59e0b',
          width: 2
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(245, 158, 11, 0.3)' },
              { offset: 1, color: 'rgba(245, 158, 11, 0.1)' }
            ]
          }
        },
        symbol: 'none',
        animation: false
      });
      legend.push(t.visualizer.ch2SeriesFP2 + ' SQI');
    }
    
    return {
      title: title ? {
        text: title,
        left: 'center',
        textStyle: {
          fontSize: 14,
          fontWeight: 'normal',
          color: '#e5e7eb'
        }
      } : undefined,
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const index = params[0].value[0];
          let result = `샘플 #${index}<br/>`;
          params.forEach((param: any) => {
            result += `${param.seriesName}: ${param.value[1].toFixed(1)}%<br/>`;
          });
          return result;
        }
      },
      legend: legend.length > 1 ? {
        data: legend,
        top: title ? 30 : 10
      } : undefined,
      grid: {
        left: '10%',
        right: '10%',
        bottom: '15%',
        top: title ? (legend.length > 1 ? '20%' : '15%') : (legend.length > 1 ? '15%' : '10%')
      },
      xAxis: {
        type: 'value',
        name: t.visualizer.xAxisTime,
        nameLocation: 'middle',
        nameGap: 25,
        axisLabel: {
          show: false // X축 값 숨기기
        }
      },
      yAxis: {
        type: 'value',
        name: t.visualizer.yAxisSignalQuality,
        nameLocation: 'middle',
        nameGap: 40,
        min: 0,
        max: 100,
        axisLabel: {
          formatter: '{value}%'
        }
      },
      series: series,
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'none'
        }
      ]
    };
  }, [channel, title]);

  // EEG 분석 결과 기반 SQI 데이터 처리 (Index 기반)
  const chartData = useMemo(() => {
    if (!eegSQIData) {
      return { ch1: [], ch2: [], hasData: false };
    }

    // 데이터가 있으면 연결 상태와 관계없이 표시
    if (!hasRealData) {
      return { ch1: [], ch2: [], hasData: false };
    }

    // 최근 1000개 샘플만 표시 (약 4초 분량)
    const maxDisplaySamples = 1000;
    
    // Index 기반 데이터로 변경 - [index, value] 형태
    const ch1Data = eegSQIData.ch1SQI
      .slice(-maxDisplaySamples)
      .map((point: any, index: number) => [index, point.value]);
    
    const ch2Data = eegSQIData.ch2SQI
      .slice(-maxDisplaySamples)
      .map((point: any, index: number) => [index, point.value]);

    return {
      ch1: ch1Data,
      ch2: ch2Data,
      hasData: ch1Data.length > 0 || ch2Data.length > 0
    };
  }, [eegSQIData, hasRealData]);

  // 차트 초기화
  useEffect(() => {
    if (!chartRef.current) return;

    chartInstance.current = echarts.init(chartRef.current);
    chartInstance.current.setOption(chartOption);
    setIsInitialized(true);

    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
    };
  }, [chartOption]);

  // EEG 분석 결과 기반 SQI 데이터 업데이트
  useEffect(() => {
    if (!chartInstance.current || !isInitialized) return;

    const updateChart = () => {
      if (!chartData.hasData) {
        return;
      }

      const { ch1, ch2 } = chartData;
      
      const seriesData = [];
      if (channel === 'both' || channel === 'ch1') {
        seriesData.push({ data: ch1 });
      }
      if (channel === 'both' || channel === 'ch2') {
        seriesData.push({ data: ch2 });
      }
      
      chartInstance.current?.setOption({
        series: seriesData
      });
    };

    updateChart();

  }, [isInitialized, chartData.hasData, chartData.ch1, chartData.ch2, channel]);

  const ch1Length = eegSQIData?.ch1SQI?.length || 0;
  const ch2Length = eegSQIData?.ch2SQI?.length || 0;

  return (
    <div className="w-full h-80 relative">
      {/* 차트 div — 항상 DOM에 존재 (ECharts 초기화를 위해) */}
      <div ref={chartRef} className="w-full h-full" />
      
      {/* 데이터 없을 때 overlay EmptyState */}
      {!hasRealData && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-10">
          <div className="text-center space-y-4">
            <div className="text-lg text-foreground">
              {t.visualizer.noGraphData}
            </div>
            <div className="text-sm text-muted-foreground">
              {t.visualizer.connectDevice}
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              {t.visualizer.connectToView.replace('{channel}', 
                channel === 'ch1' ? t.visualizer.channel1 : channel === 'ch2' ? t.visualizer.channel2 : t.visualizer.yAxisSignalQuality
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-3 p-2 bg-card rounded">
              {t.visualizer.systemInfo}:<br/>
              {t.visualizer.connectionStateLabel}: {isConnected ? t.visualizer.contactNormal : t.visualizer.contactPoor}<br/>
              {t.visualizer.ch1SQIDataLabel}: {ch1Length}개<br/>
              {t.visualizer.ch2SQIDataLabel}: {ch2Length}개<br/>
              {t.visualizer.eegSQIComputing}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignalQualityChart; 